import React, {useMemo, useState} from "react";
import {useStore, useFetch} from "@vigo/hooks";
import {Table, Spin, Button, Select, Form, Col, Input, Row} from "antd";
import {useTranslation} from "react-i18next";
import {useObserver} from "mobx-react-lite";
import {DeleteSupplierModal} from "./Modal/DeleteSupplierModal";
import {useHistory} from "react-router-dom";
import {UpdateSecretTokenModal} from "./Modal/UpdateSecretTokenModal";
import {ChangeStatusSupplierModal} from "./Modal/ChangeStatusSupplierModal";
import {IntegratorList} from "../EditSupplier/IntegratorList";
import {debounce} from "lodash";
import {formatPhoneNumber} from "../../../utils/helpers";

export const SupplierPage = ({companyId}) => {
    const stores = useStore();
    const history = useHistory();
    const [refetch, setRefetch] = useState(false);
    const [type, setType] = useState(null);
    const [isActive, setIsActive] = useState(null);
    const [integratorId, setIntegratorId] = useState(null);
    const {t} = useTranslation(["supplierPage"]);
    const [, supplierListRecords] = useFetch({
        defaultDataValue: [],
        fetchFn: stores.supplierStore.supplierList.bind(null, {
            companyId,
            type,
            isActive: isActive === true || isActive === false ? String(isActive) : null,
            integratorId,
        }),
        initialCall: true,
        showLoading: true,
        dependencies: [refetch, companyId, type, isActive, integratorId]
    });

    return useObserver(() => (
        <div>
            <h1>
                Tedarikçi Listesi{" "}
                {
                    stores.appStore.companyListFilter.find(c => c._id.toString() === companyId)
                        ?.companyName
                }
            </h1>
            <Row
                style={{
                    gap: "20px"
                }}
            >
                <Col
                    style={{
                        flex: 1,
                        width: "100%"
                    }}
                >
                    <h5>{t("fields.integrator", {ns: "supplierDetail"})}</h5>
                    <IntegratorList
                        onSelect={value => {
                            setIntegratorId(value);
                        }}
                        type={type}
                        value={integratorId}
                        style={{
                            width: "100%"
                        }}
                    />
                </Col>
                <Col
                    style={{
                        flex: 1,
                        width: "100%"
                    }}
                >
                    <h5>{t("fields.type", {ns: "supplierDetail"})}</h5>
                    <Select
                        name="type"
                        value={type}
                        allowClear
                        onChange={value => setType(value)}
                        style={{
                            width: "100%"
                        }}
                    >
                        {["cargo", "pos", "c2c"].map(type => (
                            <Select.Option key={type} value={type} name="type">
                                {t(`types.${type}`, {
                                    ns: "supplierDetail"
                                })}
                            </Select.Option>
                        ))}
                    </Select>
                </Col>
                <Col
                    style={{
                        flex: 1,
                        width: "100%"
                    }}
                >
                    <h5>{t("fields.isActive", {ns: "supplierDetail"})}</h5>
                    <Select
                        name="isActive"
                        allowClear
                        value={isActive}
                        onChange={value => setIsActive(value)}
                        style={{
                            width: "100%"
                        }}
                    >
                        <Select.Option value={true} name="type">
                            {"Aktif"}
                        </Select.Option>
                        <Select.Option value={false} name="type">
                            {"Pasif"}
                        </Select.Option>
                    </Select>
                </Col>
            </Row>
            <Button
                onClick={() => {
                    history.push(`/new-supplier/${companyId}`);
                }}
                style={{
                    marginTop: "20px",
                    marginBottom: "20px"
                }}
                type={"primary"}
            >
                Yeni Tedarikçi
            </Button>

            <Spin spinning={false}>
                <Table
                    rowKey="_id"
                    dataSource={supplierListRecords?.data ?? []}
                    data-testid="supplierTable"
                >
                    <Table.Column
                        dataIndex="integrator.type"
                        title={t("fields.type", {ns: "supplierDetail"})}
                        render={(_, {integrator}) => (
                            <span data-testid="supplierPage">
                                {t(`types.${integrator.type}`, {ns: "supplierDetail"})}
                            </span>
                        )}
                    />
                    <Table.Column
                        dataIndex="integrator.name"
                        title={t("fields.integrator", {ns: "supplierDetail"})}
                        render={(_, {integrator}) => (
                            <span data-testid="supplierPage">{integrator?.name}</span>
                        )}
                    />
                    <Table.Column
                        dataIndex="authorizedName"
                        title={t("fields.authorizedName", {ns: "supplierDetail"})}
                        render={text => <span data-testid="supplierPage">{text}</span>}
                    />
                    <Table.Column
                        dataIndex="authorizedPhone"
                        title={t("fields.authorizedPhone", {ns: "supplierDetail"})}
                        render={text => (
                            <span data-testid="supplierPage">{formatPhoneNumber(text)}</span>
                        )}
                    />
                    <Table.Column
                        dataIndex="email"
                        title={t("fields.email", {ns: "supplierDetail"})}
                        render={email => <span data-testid="supplierPage">{email}</span>}
                    />
                    <Table.Column
                        dataIndex="isActive"
                        title={t("fields.isActive", {ns: "supplierDetail"})}
                        render={(isActive, supplier) => (
                            <span data-testid="supplierPage">
                                <ChangeStatusSupplierModal
                                    supplierId={supplier._id}
                                    setRefetch={setRefetch}
                                    isActive={isActive}
                                />
                            </span>
                        )}
                    />
                    <Table.Column
                        title={t("fields.settings", {ns: "supplierDetail"})}
                        render={(text, supplier) => (
                            <div
                                style={{
                                    display: "flex"
                                }}
                            >
                                <span data-testid="supplierPage"></span>
                                <Button
                                    style={{
                                        margin: "8px"
                                    }}
                                    data-testid="supplierPage"
                                    onClick={() => {
                                        history.push("/edit-supplier/" + supplier._id);
                                    }}
                                >
                                    Düzenle
                                </Button>
                                <UpdateSecretTokenModal
                                    supplierId={supplier._id}
                                    setRefetch={setRefetch}
                                />
                                <DeleteSupplierModal
                                    supplierId={supplier._id}
                                    setRefetch={setRefetch}
                                />
                            </div>
                        )}
                    />
                </Table>
            </Spin>
        </div>
    ));
};
